import { useState } from 'react';

export default function WorksheetReframingHero() {
  const [showModal, setShowModal] = useState(false);

  const scrollToPratica = () => {
    const praticaSection = document.getElementById('pratica-reframing');
    if (praticaSection) {
      praticaSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <section className="relative bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full mb-6 shadow-sm">
              <i className="ri-refresh-line text-purple-600 text-xl"></i>
              <span className="text-sm font-medium text-gray-700">Técnica de PNL</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Worksheet de
              <span className="block bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 bg-clip-text text-transparent mt-2">
                Ressignificação (Reframing)
              </span>
            </h1>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Transforme o significado de qualquer situação e liberte-se de interpretações limitantes.
              <span className="block mt-2 font-medium text-gray-700">
                Mude a perspectiva, mude sua vida.
              </span>
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
              <button
                onClick={scrollToPratica}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-xl transition-all duration-300 hover:scale-105 whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-play-circle-line text-2xl"></i>
                Começar Agora
              </button>
              <button
                onClick={() => setShowModal(true)}
                className="bg-white text-purple-600 px-8 py-4 rounded-full font-semibold text-lg hover:shadow-lg transition-all duration-300 border-2 border-purple-200 whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-information-line text-xl"></i>
                Como Funciona
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-sm">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-lightbulb-flash-line text-white text-2xl"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Poderoso</h3>
                <p className="text-sm text-gray-600">Mude significados em minutos</p>
              </div>

              <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-sm">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-brain-line text-white text-2xl"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Científico</h3>
                <p className="text-sm text-gray-600">Baseado em PNL comprovada</p>
              </div>

              <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-sm">
                <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-orange-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-heart-pulse-line text-white text-2xl"></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Libertador</h3>
                <p className="text-sm text-gray-600">Liberte-se de interpretações limitantes</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold">Como Funciona o Reframing</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-10 h-10 flex items-center justify-center hover:bg-white/20 rounded-full transition-colors"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-8 space-y-6">
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-question-line text-purple-600"></i>
                  O que é Ressignificação?
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Reframing (Ressignificação) é uma técnica de PNL que muda o significado que você dá a uma situação, 
                  sem mudar a situação em si. É como trocar a moldura de um quadro — a imagem continua a mesma, 
                  mas você a vê de forma completamente diferente.
                </p>
              </div>

              <div className="bg-purple-50 p-6 rounded-2xl">
                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-contrast-2-line text-purple-600"></i>
                  Dois Tipos de Reframing
                </h4>
                
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-xl">
                    <h5 className="font-semibold text-purple-600 mb-2">1. Reframing de Contexto</h5>
                    <p className="text-sm text-gray-700 mb-2">
                      Muda o <strong>ONDE</strong> — Em que situação esse comportamento seria útil?
                    </p>
                    <p className="text-sm text-gray-600 italic">
                      Exemplo: "Sou teimoso" → "Sou persistente quando acredito em algo importante"
                    </p>
                  </div>

                  <div className="bg-white p-4 rounded-xl">
                    <h5 className="font-semibold text-pink-600 mb-2">2. Reframing de Conteúdo</h5>
                    <p className="text-sm text-gray-700 mb-2">
                      Muda o <strong>QUE</strong> — Que outro significado essa situação pode ter?
                    </p>
                    <p className="text-sm text-gray-600 italic">
                      Exemplo: "Fui rejeitado" → "Fui redirecionado para algo melhor"
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-heart-3-line text-pink-600"></i>
                  Princípio Fundamental
                </h4>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Todo comportamento tem uma <strong>intenção positiva</strong>. Mesmo comportamentos que parecem 
                  negativos estão tentando proteger você ou satisfazer uma necessidade importante.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Quando você descobre essa intenção positiva, pode encontrar formas melhores de satisfazê-la.
                </p>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-2xl">
                <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-magic-line text-purple-600"></i>
                  Por que funciona?
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Não são os eventos que nos afetam, mas o <strong>significado</strong> que damos a eles. 
                  Ao mudar o significado, você muda automaticamente como se sente e como age.
                </p>
              </div>

              <button
                onClick={() => setShowModal(false)}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 whitespace-nowrap"
              >
                Entendi, vamos começar!
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}